import axios from "axios";
import { showToast } from "../utils/utils";
import { AUTH_ENDPOINTS, AUTH_PUBLIC_API_ROUTES } from "../store/authConfig";
import { clearAuth, setAccessToken, setRefreshToken } from "../store/authSlice";

let store;

export const injectStore = (_store) => {
  store = _store;
};

const getAccessToken = () => store?.getState()?.auth?.accessToken || null;
const getRefreshToken = () => store?.getState()?.auth?.refreshToken || null;

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  store?.dispatch(clearAuth());
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const isPublicRoute = (url = "") =>
  AUTH_PUBLIC_API_ROUTES.some((route) => url.includes(route));

const isRefreshRequest = (url = "") => url.includes("/refresh");

/* REQUEST INTERCEPTOR */
api.interceptors.request.use(
  (config) => {
    if (!isPublicRoute(config.url)) {
      const token = getAccessToken();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (timezone) {
        config.headers["X-TIMEZONE"] = timezone;
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* RESPONSE INTERCEPTOR */
api.interceptors.response.use(
  (response) => {
    const skipToast = response.config?.skipToast;

    if (!skipToast && response?.data?.message) {
      showToast(response.data.message, "success");
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const skipToast = originalRequest?.skipToast;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest.url)
    ) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        if (!skipToast) {
          showToast("Session expired. Please login again.", "error");
        }
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(Promise.reject);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post(
          AUTH_ENDPOINTS.refresh,
          { refresh: refreshToken },
          { skipToast: true }
        );

        const newAccessToken = data?.access;
        const newRefreshToken = data?.refresh;

        if (!newAccessToken) {
          throw new Error("Refresh token invalid");
        }

        store?.dispatch(setAccessToken(newAccessToken));
        if (newRefreshToken) {
          store?.dispatch(setRefreshToken(newRefreshToken));
        }
        api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        forceLogout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    const message = error.response?.data?.message || "Something went wrong";
    if (!skipToast) {
      if (status === 403) {
        showToast("You are not authorized", "warning");
      } else if (status >= 500) {
        showToast("Server error. Please try again later", "error");
      } else {
        showToast(message, "error");
      }
    }

    return Promise.reject(error);
  }
);

const service = {
  get: (url, params = {}, config = {}) => api.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) => api.post(url, data, config),

  put: (url, data = {}, config = {}) => api.put(url, data, config),

  patch: (url, data = {}, config = {}) => api.patch(url, data, config),

  delete: (url, config = {}) => api.delete(url, config),

  upload: (url, data, config = {}) =>
    api.post(url, data, {
      headers: { "Content-Type": "multipart/form-data" },
      ...config,
    }),
};

export default service;
